import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { renameTarget } from "@/lib/boo-rename";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, Pencil, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

// The Boo's name in its header. Boo admins and owners (canConfigure) get a
// pencil to rename it in place; everyone else sees plain text.
export function BooNameEditor({
  containerId,
  name,
  canRename,
}: {
  containerId: string;
  name: string;
  canRename: boolean;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);

  const rename = useMutation({
    mutationFn: (newName: string) => api.containers.rename(containerId, newName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["containers"] });
      setEditing(false);
      toast.success("Boo renamed");
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not rename this Boo"),
  });

  const target = renameTarget(draft, name);
  const save = () => {
    if (target) rename.mutate(target);
    else setEditing(false);
  };
  const cancel = () => {
    setDraft(name);
    setEditing(false);
  };

  if (!editing) {
    return (
      <>
        <span className="text-xl font-semibold tracking-tight truncate">{name}</span>
        {canRename && (
          <Button
            size="icon"
            variant="ghost"
            className="size-7 shrink-0 text-muted-foreground"
            title="Rename Boo"
            onClick={() => {
              setDraft(name);
              setEditing(true);
            }}
          >
            <Pencil className="size-3.5" />
            <span className="sr-only">Rename Boo</span>
          </Button>
        )}
      </>
    );
  }

  return (
    <form
      className="flex items-center gap-1"
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      <Input
        autoFocus
        aria-label="Boo name"
        value={draft}
        maxLength={100}
        disabled={rename.isPending}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") cancel();
        }}
        className="h-8 w-64 text-base font-semibold"
      />
      <Button type="submit" size="icon" variant="ghost" className="size-8" loading={rename.isPending} disabled={!draft.trim()} title="Save name">
        <Check className="size-4" />
        <span className="sr-only">Save name</span>
      </Button>
      <Button type="button" size="icon" variant="ghost" className="size-8" disabled={rename.isPending} onClick={cancel} title="Cancel">
        <X className="size-4" />
        <span className="sr-only">Cancel</span>
      </Button>
    </form>
  );
}
